import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CardRowItem,
  CardRowSection,
  EditorialEntry,
  EditorialListSection,
  FeatureAccent,
  FeatureGridItem,
  FeatureGridSection,
  LandingSection,
  ProductSpotlightSection,
  SplitMediaSection,
} from '../../../../../components/storefront/landingSectionsCms';

const ACCENTS: FeatureAccent[] = ['teal', 'purple', 'amber', 'rose', 'emerald', 'orange'];
const MAX_CARD_ITEMS = 8;
const MAX_EDITORIAL_ENTRIES = 10;

function cloneSections(sections: LandingSection[]): LandingSection[] {
  return JSON.parse(JSON.stringify(sections)) as LandingSection[];
}

function resolveMediaUrl(raw: string, apiBase: string): string {
  const t = (raw || '').trim();
  if (!t) return '';
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('/uploads/')) return `${apiBase.replace(/\/$/, '')}${t}`;
  return t;
}

function replaceAt<T>(arr: T[], index: number, item: T): T[] {
  const next = [...arr];
  next[index] = item;
  return next;
}

function moveIndex<T>(arr: T[], from: number, to: number): T[] {
  if (to < 0 || to >= arr.length || from === to) return arr;
  const next = [...arr];
  const [removed] = next.splice(from, 1);
  next.splice(to, 0, removed);
  return next;
}

function inputClass(readonly?: boolean) {
  return `mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm ${readonly ? 'bg-slate-50 text-slate-600' : 'bg-white'}`;
}

function LandingImageField({
  label,
  value,
  onChange,
  authorizedFetch,
  apiBase,
  previewClassName = 'max-h-44 w-full max-w-md rounded-lg border border-slate-200 object-cover',
}: {
  label: string;
  value: string;
  onChange: (url: string) => void;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  apiBase: string;
  previewClassName?: string;
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const full = resolveMediaUrl(value, apiBase);

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authorizedFetch(`${apiBase}/settings/site-content/landing-image`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { message?: string }).message || 'Upload failed';
        throw new Error(typeof msg === 'string' ? msg : 'Upload failed');
      }
      const json = (await res.json()) as { url?: string };
      if (!json.url) throw new Error('Server did not return image URL');
      onChange(json.url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold text-slate-700">{label}</p>
      {full ? (
        <div className="mt-2">
          <img src={full} alt="" className={previewClassName} />
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-slate-500">
          {t('landing_editor_no_image', {
            defaultValue: 'No image yet — paste a URL or upload a file.',
          })}
        </p>
      )}
      <label className="mt-2 block text-[11px] font-medium text-slate-600">
        {t('landing_editor_image_url_label', {
          defaultValue: 'Image URL (optional if you upload)',
        })}
        <input
          type="url"
          className={inputClass()}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('landing_editor_image_url_placeholder', {
            defaultValue: 'https://… or /uploads/…',
          })}
        />
      </label>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100">
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onPickFile} disabled={uploading} />
          {uploading
            ? t('landing_editor_uploading', { defaultValue: 'Uploading…' })
            : t('landing_editor_upload_image', { defaultValue: 'Upload image' })}
        </label>
        {value ? (
          <button
            type="button"
            className="text-xs font-semibold text-rose-600 hover:underline"
            onClick={() => onChange('')}
          >
            {t('common.clear', { defaultValue: 'Clear' })}
          </button>
        ) : null}
      </div>
      {uploadError ? <p className="mt-1 text-xs text-rose-600">{uploadError}</p> : null}
    </div>
  );
}

function SplitMediaEditor({
  section,
  onChange,
  authorizedFetch,
  apiBase,
}: {
  section: SplitMediaSection;
  onChange: (s: SplitMediaSection) => void;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  apiBase: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
        {t('landing_editor_kicker_label', {
          defaultValue: 'Kicker (small label above headline)',
        })}
        <input className={inputClass()} value={section.kicker ?? ''} onChange={(e) => onChange({ ...section, kicker: e.target.value })} />
      </label>
      <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
        {t('landing_editor_headline_label', { defaultValue: 'Headline' })}
        <input className={inputClass()} value={section.title} onChange={(e) => onChange({ ...section, title: e.target.value })} />
      </label>
      <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
        {t('landing_editor_body_label', { defaultValue: 'Body' })}
        <textarea
          rows={5}
          className={inputClass()}
          value={section.body}
          onChange={(e) => onChange({ ...section, body: e.target.value })}
        />
      </label>
      <div className="sm:col-span-2">
        <LandingImageField
          label={t('landing_editor_hero_image', { defaultValue: 'Hero image' })}
          value={section.imageUrl}
          onChange={(imageUrl) => onChange({ ...section, imageUrl })}
          authorizedFetch={authorizedFetch}
          apiBase={apiBase}
        />
      </div>
      <label className="text-xs font-semibold text-slate-700">
        {t('landing_editor_image_alt', { defaultValue: 'Image alt text' })}
        <input className={inputClass()} value={section.imageAlt ?? ''} onChange={(e) => onChange({ ...section, imageAlt: e.target.value })} />
      </label>
      <label className="text-xs font-semibold text-slate-700">
        {t('landing_editor_image_position', { defaultValue: 'Image position' })}
        <select
          className={inputClass()}
          value={section.imagePosition ?? 'right'}
          onChange={(e) =>
            onChange({
              ...section,
              imagePosition: e.target.value === 'left' ? 'left' : 'right',
            })
          }
        >
          <option value="right">
            {t('landing_editor_image_position_right', {
              defaultValue: 'Right (text first on desktop)',
            })}
          </option>
          <option value="left">
            {t('landing_editor_image_position_left', {
              defaultValue: 'Left (image first on desktop)',
            })}
          </option>
        </select>
      </label>
      <label className="text-xs font-semibold text-slate-700">
        {t('landing_editor_background', { defaultValue: 'Background' })}
        <select
          className={inputClass()}
          value={section.background ?? 'default'}
          onChange={(e) =>
            onChange({
              ...section,
              background: e.target.value as SplitMediaSection['background'],
            })
          }
        >
          <option value="default">{t('common.default', { defaultValue: 'Default' })}</option>
          <option value="muted">{t('common.muted', { defaultValue: 'Muted' })}</option>
          <option value="warm">{t('common.warm', { defaultValue: 'Warm' })}</option>
        </select>
      </label>
      <label className="text-xs font-semibold text-slate-700">
        {t('landing_editor_button_label', { defaultValue: 'Button label' })}
        <input className={inputClass()} value={section.ctaLabel ?? ''} onChange={(e) => onChange({ ...section, ctaLabel: e.target.value })} />
      </label>
      <label className="text-xs font-semibold text-slate-700">
        {t('landing_editor_button_link', { defaultValue: 'Button link' })}
        <input className={inputClass()} value={section.ctaHref ?? ''} onChange={(e) => onChange({ ...section, ctaHref: e.target.value })} placeholder="/products" />
      </label>
    </div>
  );
}

function FeatureGridEditor({
  section,
  onChange,
}: {
  section: FeatureGridSection;
  onChange: (s: FeatureGridSection) => void;
}) {
  const { t } = useTranslation();
  const setItem = (i: number, item: FeatureGridItem) => {
    onChange({ ...section, items: replaceAt(section.items, i, item) });
  };
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('landing_editor_eyebrow', { defaultValue: 'Eyebrow' })}
          <input className={inputClass()} value={section.eyebrow ?? ''} onChange={(e) => onChange({ ...section, eyebrow: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('common.title', { defaultValue: 'Title' })}
          <input className={inputClass()} value={section.title} onChange={(e) => onChange({ ...section, title: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('common.subtitle', { defaultValue: 'Subtitle' })}
          <input className={inputClass()} value={section.subtitle ?? ''} onChange={(e) => onChange({ ...section, subtitle: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700">
          {t('landing_editor_columns', { defaultValue: 'Columns' })}
          <select
            className={inputClass()}
            value={section.columns ?? 3}
            onChange={(e) => onChange({ ...section, columns: Number(e.target.value) as 2 | 3 | 4 })}
          >
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
          </select>
        </label>
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {t('landing_editor_feature_cards', { defaultValue: 'Feature cards' })}
      </p>
      <div className="space-y-3">
        {section.items.map((it, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
            <p className="text-[11px] font-bold text-slate-600">
              {t('landing_editor_card_n', { defaultValue: 'Card {{n}}', n: i + 1 })}
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-700">
                {t('landing_editor_icon_class', {
                  defaultValue: 'Icon (Font Awesome class)',
                })}
                <input
                  className={inputClass()}
                  value={it.icon}
                  onChange={(e) => setItem(i, { ...it, icon: e.target.value })}
                  placeholder="fa-truck"
                />
              </label>
              <label className="text-xs font-semibold text-slate-700">
                {t('landing_editor_accent', { defaultValue: 'Accent' })}
                <select
                  className={inputClass()}
                  value={it.accent}
                  onChange={(e) => setItem(i, { ...it, accent: e.target.value as FeatureAccent })}
                >
                  {ACCENTS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
                {t('common.title', { defaultValue: 'Title' })}
                <input className={inputClass()} value={it.title} onChange={(e) => setItem(i, { ...it, title: e.target.value })} />
              </label>
              <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
                {t('landing_editor_body_label', { defaultValue: 'Body' })}
                <textarea rows={3} className={inputClass()} value={it.body} onChange={(e) => setItem(i, { ...it, body: e.target.value })} />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CardRowEditor({
  section,
  onChange,
  authorizedFetch,
  apiBase,
}: {
  section: CardRowSection;
  onChange: (s: CardRowSection) => void;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  apiBase: string;
}) {
  const { t } = useTranslation();
  const setItem = (i: number, item: CardRowItem) => {
    onChange({ ...section, items: replaceAt(section.items, i, item) });
  };
  const addItem = () => {
    if (section.items.length >= MAX_CARD_ITEMS) return;
    onChange({
      ...section,
      items: [
        ...section.items,
        {
          title: t('landing_editor_new_item_title', { defaultValue: 'New item' }),
          imageUrl: '',
          imageAlt: '',
          ctaLabel: t('landing_editor_shop_now', { defaultValue: 'Shop now' }),
          ctaHref: '/products',
        },
      ],
    });
  };
  const removeItem = (i: number) => {
    if (section.items.length <= 1) return;
    onChange({ ...section, items: section.items.filter((_, j) => j !== i) });
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('landing_editor_eyebrow', { defaultValue: 'Eyebrow' })}
          <input className={inputClass()} value={section.eyebrow ?? ''} onChange={(e) => onChange({ ...section, eyebrow: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('common.title', { defaultValue: 'Title' })}
          <input className={inputClass()} value={section.title ?? ''} onChange={(e) => onChange({ ...section, title: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('common.subtitle', { defaultValue: 'Subtitle' })}
          <input className={inputClass()} value={section.subtitle ?? ''} onChange={(e) => onChange({ ...section, subtitle: e.target.value })} />
        </label>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {t('landing_editor_ingredient_cards_max', {
            defaultValue: 'Ingredient cards (max {{count}})',
            count: MAX_CARD_ITEMS,
          })}
        </p>
        <button
          type="button"
          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40"
          onClick={addItem}
          disabled={section.items.length >= MAX_CARD_ITEMS}
        >
          {t('landing_editor_add_card', { defaultValue: 'Add card' })}
        </button>
      </div>
      <div className="space-y-4">
        {section.items.map((it, i) => (
          <div key={i} className="rounded-lg border border-emerald-200/60 bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-slate-800">
                {t('landing_editor_card_n', { defaultValue: 'Card {{n}}', n: i + 1 })}
              </p>
              <button
                type="button"
                className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-30"
                onClick={() => removeItem(i)}
                disabled={section.items.length <= 1}
              >
                {t('common.remove', { defaultValue: 'Remove' })}
              </button>
            </div>
            <label className="mt-2 block text-xs font-semibold text-slate-700">
              {t('landing_editor_title_on_image', { defaultValue: 'Title on image' })}
              <input className={inputClass()} value={it.title} onChange={(e) => setItem(i, { ...it, title: e.target.value })} />
            </label>
            <div className="mt-2">
              <LandingImageField
                label={t('landing_editor_card_image', { defaultValue: 'Card image' })}
                value={it.imageUrl}
                onChange={(imageUrl) => setItem(i, { ...it, imageUrl })}
                authorizedFetch={authorizedFetch}
                apiBase={apiBase}
                previewClassName="aspect-[4/5] max-h-52 w-full max-w-xs rounded-lg border border-slate-200 object-cover"
              />
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-slate-700">
                {t('landing_editor_image_alt', { defaultValue: 'Image alt' })}
                <input className={inputClass()} value={it.imageAlt ?? ''} onChange={(e) => setItem(i, { ...it, imageAlt: e.target.value })} />
              </label>
              <label className="text-xs font-semibold text-slate-700">
                {t('landing_editor_button_label', { defaultValue: 'Button label' })}
                <input className={inputClass()} value={it.ctaLabel ?? ''} onChange={(e) => setItem(i, { ...it, ctaLabel: e.target.value })} />
              </label>
              <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
                {t('landing_editor_button_link', { defaultValue: 'Button link' })}
                <input className={inputClass()} value={it.ctaHref ?? ''} onChange={(e) => setItem(i, { ...it, ctaHref: e.target.value })} />
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ProductSpotlightEditor({
  section,
  onChange,
}: {
  section: ProductSpotlightSection;
  onChange: (s: ProductSpotlightSection) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <p className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-xs leading-relaxed text-emerald-950">
        {t('landing_editor_product_spotlight_help', {
          defaultValue:
            'Products load automatically from your catalog. On the global storefront, picks are mixed across departments when possible.',
        })}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('landing_editor_eyebrow', { defaultValue: 'Eyebrow' })}
          <input
            className={inputClass()}
            value={section.eyebrow ?? ''}
            onChange={(e) => onChange({ ...section, eyebrow: e.target.value })}
          />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('landing_editor_section_title', { defaultValue: 'Section title' })}
          <input
            className={inputClass()}
            value={section.title}
            onChange={(e) => onChange({ ...section, title: e.target.value })}
          />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('common.subtitle', { defaultValue: 'Subtitle' })}
          <input
            className={inputClass()}
            value={section.subtitle ?? ''}
            onChange={(e) => onChange({ ...section, subtitle: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}

function EditorialListEditor({
  section,
  onChange,
  authorizedFetch,
  apiBase,
}: {
  section: EditorialListSection;
  onChange: (s: EditorialListSection) => void;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  apiBase: string;
}) {
  const { t } = useTranslation();
  const setFeatured = (patch: Partial<EditorialListSection['featured']>) => {
    onChange({ ...section, featured: { ...section.featured, ...patch } });
  };
  const setEntry = (i: number, entry: EditorialEntry) => {
    onChange({ ...section, entries: replaceAt(section.entries, i, entry) });
  };
  const addEntry = () => {
    if (section.entries.length >= MAX_EDITORIAL_ENTRIES) return;
    onChange({
      ...section,
      entries: [
        ...section.entries,
        { date: '', title: '', imageUrl: '', href: '/products' },
      ],
    });
  };
  const removeEntry = (i: number) => {
    onChange({ ...section, entries: section.entries.filter((_, j) => j !== i) });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('landing_editor_eyebrow', { defaultValue: 'Eyebrow' })}
          <input className={inputClass()} value={section.eyebrow ?? ''} onChange={(e) => onChange({ ...section, eyebrow: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('landing_editor_section_title', { defaultValue: 'Section title' })}
          <input className={inputClass()} value={section.title} onChange={(e) => onChange({ ...section, title: e.target.value })} />
        </label>
        <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
          {t('common.subtitle', { defaultValue: 'Subtitle' })}
          <input className={inputClass()} value={section.subtitle ?? ''} onChange={(e) => onChange({ ...section, subtitle: e.target.value })} />
        </label>
      </div>

      <div className="rounded-lg border border-slate-300 bg-slate-50 p-4">
        <p className="text-xs font-bold text-slate-800">
          {t('landing_editor_featured_article', {
            defaultValue: 'Featured article (large)',
          })}
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-semibold text-slate-700">
            {t('landing_editor_date_line', { defaultValue: 'Date line' })}
            <input className={inputClass()} value={section.featured.date} onChange={(e) => setFeatured({ date: e.target.value })} />
          </label>
          <label className="text-xs font-semibold text-slate-700">
            {t('common.link', { defaultValue: 'Link' })}
            <input className={inputClass()} value={section.featured.href ?? ''} onChange={(e) => setFeatured({ href: e.target.value })} />
          </label>
          <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
            {t('common.title', { defaultValue: 'Title' })}
            <input className={inputClass()} value={section.featured.title} onChange={(e) => setFeatured({ title: e.target.value })} />
          </label>
          <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
            {t('landing_editor_excerpt_body', { defaultValue: 'Excerpt / body' })}
            <textarea rows={4} className={inputClass()} value={section.featured.body ?? ''} onChange={(e) => setFeatured({ body: e.target.value })} />
          </label>
          <div className="sm:col-span-2">
            <LandingImageField
              label={t('landing_editor_featured_image', {
                defaultValue: 'Featured image',
              })}
              value={section.featured.imageUrl}
              onChange={(imageUrl) => setFeatured({ imageUrl })}
              authorizedFetch={authorizedFetch}
              apiBase={apiBase}
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {t('landing_editor_sidebar_articles_max', {
              defaultValue: 'Sidebar articles (max {{count}})',
              count: MAX_EDITORIAL_ENTRIES,
            })}
          </p>
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40"
            onClick={addEntry}
            disabled={section.entries.length >= MAX_EDITORIAL_ENTRIES}
          >
            {t('landing_editor_add_article', { defaultValue: 'Add article' })}
          </button>
        </div>
        <div className="mt-3 space-y-3">
          {section.entries.map((en, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-slate-700">
                  {t('landing_editor_article_n', {
                    defaultValue: 'Article {{n}}',
                    n: i + 1,
                  })}
                </p>
                <button type="button" className="text-xs font-semibold text-rose-600 hover:underline" onClick={() => removeEntry(i)}>
                  {t('common.remove', { defaultValue: 'Remove' })}
                </button>
              </div>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-slate-700">
                  {t('common.date', { defaultValue: 'Date' })}
                  <input className={inputClass()} value={en.date} onChange={(e) => setEntry(i, { ...en, date: e.target.value })} />
                </label>
                <label className="text-xs font-semibold text-slate-700">
                  {t('common.link', { defaultValue: 'Link' })}
                  <input className={inputClass()} value={en.href ?? ''} onChange={(e) => setEntry(i, { ...en, href: e.target.value })} />
                </label>
                <label className="text-xs font-semibold text-slate-700 sm:col-span-2">
                  {t('common.title', { defaultValue: 'Title' })}
                  <input className={inputClass()} value={en.title} onChange={(e) => setEntry(i, { ...en, title: e.target.value })} />
                </label>
                <div className="sm:col-span-2">
                  <LandingImageField
                    label={t('landing_editor_thumbnail', { defaultValue: 'Thumbnail' })}
                    value={en.imageUrl ?? ''}
                    onChange={(imageUrl) => setEntry(i, { ...en, imageUrl })}
                    authorizedFetch={authorizedFetch}
                    apiBase={apiBase}
                    previewClassName="h-24 w-28 rounded-lg border border-slate-200 object-cover"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function sectionTypeLabel(
  translate: (key: string, options?: Record<string, unknown>) => string,
  type: LandingSection['type'],
): string {
  switch (type) {
    case 'SPLIT_MEDIA':
      return translate('landing_editor_section_type_split_media', {
        defaultValue: 'Story + image',
      });
    case 'FEATURE_GRID':
      return translate('landing_editor_section_type_feature_grid', {
        defaultValue: 'Service grid',
      });
    case 'CARD_ROW':
      return translate('landing_editor_section_type_card_row', {
        defaultValue: 'Image cards row',
      });
    case 'EDITORIAL_LIST':
      return translate('landing_editor_section_type_editorial_list', {
        defaultValue: 'Journal / articles',
      });
    case 'PRODUCT_SPOTLIGHT':
      return translate('landing_editor_section_type_product_spotlight', {
        defaultValue: 'Live product spotlight',
      });
    default:
      return type;
  }
}

export default function LandingSectionsEditor({
  sections,
  onChange,
  authorizedFetch,
  apiBase,
  onResetDefaults,
}: {
  sections: LandingSection[];
  onChange: (next: LandingSection[]) => void;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  apiBase: string;
  onResetDefaults: () => void;
}) {
  const { t } = useTranslation();
  const [advancedJson, setAdvancedJson] = React.useState('');
  const [advancedError, setAdvancedError] = React.useState<string | null>(null);

  const applySection = (index: number, next: LandingSection) => {
    onChange(replaceAt(sections, index, next));
  };

  const applyAdvancedJson = () => {
    setAdvancedError(null);
    try {
      const parsed = JSON.parse(advancedJson) as unknown;
      if (!Array.isArray(parsed)) {
        setAdvancedError(
          t('landing_editor_json_root_array', {
            defaultValue: 'Root must be a JSON array.',
          }),
        );
        return;
      }
      onChange(parsed as LandingSection[]);
      setAdvancedJson(JSON.stringify(parsed, null, 2));
    } catch {
      setAdvancedError(
        t('landing_editor_json_invalid', {
          defaultValue: 'Invalid JSON.',
        }),
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            {t('landing_editor_title', {
              defaultValue: 'Storefront landing sections',
            })}
          </h2>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-600">
            {t('landing_editor_subtitle', {
              defaultValue:
                'Edit the blocks on the public home page below the hero. Upload images for each spot, or paste an external URL. Save the page with {{saveLabel}} at the bottom to publish changes.',
              saveLabel: t('siteContent.save_content', { defaultValue: 'Save content' }),
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100"
            onClick={onResetDefaults}
          >
            {t('landing_editor_reset_seeded', {
              defaultValue: 'Reset to seeded defaults',
            })}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {sections.map((section, index) => (
          <details key={`${section.id}-${index}`} className="group rounded-xl border border-slate-200 bg-white shadow-sm open:shadow-md">
            <summary className="cursor-pointer list-none rounded-xl px-4 py-3 [&::-webkit-details-marker]:hidden">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  {sectionTypeLabel(t, section.type)}
                </span>
                <span className="text-xs text-slate-500">
                  id: <code className="rounded bg-slate-100 px-1">{section.id}</code>
                </span>
                <label className="ml-auto flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={section.enabled !== false}
                    onChange={(e) => applySection(index, { ...section, enabled: e.target.checked })}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {t('common.visible', { defaultValue: 'Visible' })}
                </label>
                <label className="flex items-center gap-1 text-xs font-semibold text-slate-700">
                  {t('common.order', { defaultValue: 'Order' })}
                  <input
                    type="number"
                    className="w-16 rounded border border-slate-300 px-2 py-1 text-xs"
                    value={section.sortOrder ?? 0}
                    onChange={(e) => applySection(index, { ...section, sortOrder: Number(e.target.value) || 0 })}
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-30"
                    disabled={index === 0}
                    onClick={() => onChange(moveIndex(sections, index, index - 1))}
                    title={t('common.move_up', { defaultValue: 'Move up' })}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-30"
                    disabled={index >= sections.length - 1}
                    onClick={() => onChange(moveIndex(sections, index, index + 1))}
                    title={t('common.move_down', { defaultValue: 'Move down' })}
                  >
                    ↓
                  </button>
                </div>
              </div>
            </summary>
            <div className="border-t border-slate-100 px-4 py-4">
              {section.type === 'SPLIT_MEDIA' ? (
                <SplitMediaEditor
                  section={section}
                  onChange={(s) => applySection(index, s)}
                  authorizedFetch={authorizedFetch}
                  apiBase={apiBase}
                />
              ) : null}
              {section.type === 'FEATURE_GRID' ? (
                <FeatureGridEditor section={section} onChange={(s) => applySection(index, s)} />
              ) : null}
              {section.type === 'CARD_ROW' ? (
                <CardRowEditor
                  section={section}
                  onChange={(s) => applySection(index, s)}
                  authorizedFetch={authorizedFetch}
                  apiBase={apiBase}
                />
              ) : null}
              {section.type === 'PRODUCT_SPOTLIGHT' ? (
                <ProductSpotlightEditor
                  section={section}
                  onChange={(s) => applySection(index, s)}
                />
              ) : null}
              {section.type === 'EDITORIAL_LIST' ? (
                <EditorialListEditor
                  section={section}
                  onChange={(s) => applySection(index, s)}
                  authorizedFetch={authorizedFetch}
                  apiBase={apiBase}
                />
              ) : null}
            </div>
          </details>
        ))}
      </div>

      <details
        className="rounded-lg border border-dashed border-slate-300 bg-slate-50/80 p-3"
        onToggle={(e) => {
          const el = e.currentTarget;
          if (el.open) {
            setAdvancedJson(JSON.stringify(sections, null, 2));
            setAdvancedError(null);
          }
        }}
      >
        <summary className="cursor-pointer text-xs font-semibold text-slate-700">
          {t('landing_editor_advanced_json_title', {
            defaultValue: 'Advanced: edit raw JSON',
          })}
        </summary>
        <p className="mt-2 text-[11px] text-slate-600">
          {t('landing_editor_advanced_json_help', {
            defaultValue:
              'For power users. Click {{applyLabel}} to merge into the fields above. The server still validates when you save the page.',
            applyLabel: t('landing_editor_apply_json', {
              defaultValue: 'Apply JSON to form',
            }),
          })}
        </p>
        <div className="mt-2 space-y-2">
          <textarea
            rows={12}
            spellCheck={false}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-800"
            value={advancedJson}
            onChange={(e) => setAdvancedJson(e.target.value)}
          />
          {advancedError ? <p className="text-xs text-rose-600">{advancedError}</p> : null}
          <button
            type="button"
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white"
            onClick={applyAdvancedJson}
          >
            {t('landing_editor_apply_json', {
              defaultValue: 'Apply JSON to form',
            })}
          </button>
        </div>
      </details>
    </div>
  );
}

export { cloneSections };
